import React, { Fragment } from "react";
import { useContext } from "react";
import { UserDetailProvider } from "@/contexts/UserDetailContext";
import { BlackButton } from "@/elements/Button/Button";
import { popUp } from "@/elements/PopUp/PopUp";
import { SideBarBlogServe } from "./SideBarBlog";
import Link from "next/link";
import { AiOutlineRight } from "react-icons/ai";
import Image from "next/image";
import moment from "moment";
import Head from "next/head";
import { unserialize } from "php-serialize";
import { useGetLocalStorage } from "@/hooks/useGetLocalStorage";
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton, WhatsappShareButton } from "react-share";
import { RiWhatsappFill } from "react-icons/ri";
import { FaFacebook } from "react-icons/fa";
import { FaLinkedin, FaXTwitter } from "react-icons/fa6";

const changeBlogs2ToBlogs = (text = "") => {
    if (text?.endsWith("/")) {
        return text?.slice(0, -1)?.replaceAll("blogs-2", "blogs");
    }
    return text?.replaceAll("blogs-2", "blogs");
};

export default function BlogDetails({ item, relatedData }) {
    const userDetail = useContext(UserDetailProvider);

    const unserializeData = (serializedPhpData) => {
        try {
            return unserialize(serializedPhpData[0]);
        } catch (error) {
            return {};
        }
    };
    const webview = useGetLocalStorage("webview");
    return (
        <>
            <Head>
                <title>{item?.metadata?.rank_math_title ?? item?.title?.rendered}</title>
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no"
                />
                <meta name="description" content={item?.metadata?.rank_math_description} />
                <meta property="og:locale" content="en_US" />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={item?.metadata?.rank_math_title} />
                <meta property="og:description" content={item?.metadata?.rank_math_description} />
                <meta property="og:url" content={changeBlogs2ToBlogs(item?.link)} />
                <meta property="og:site_name" content="https://univest.in/" />
                {item?._embedded?.["wp:term"]?.map((lists, index) => (
                    <Fragment key={index}>
                        {lists?.length > 0 &&
                            lists?.map((list) => <meta key={list.name} property="article:tag" content={list.name} />)}
                    </Fragment>
                ))}
                <link rel="canonical" href={changeBlogs2ToBlogs(item?.link)} />
                <meta property="og:updated_time" content={`${item?.modified}+05:30`} />
                <meta property="og:image" content={item?.jetpack_featured_media_url} />
                <meta property="og:image:secure_url" content={item?.jetpack_featured_media_url} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="628" />
                <meta property="og:image:alt" content={item?.metadata?.rank_math_title} />
                <meta property="og:image:type" content="image/jpeg" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={item?.metadata?.rank_math_title} />
                <meta name="twitter:description" content={item?.metadata?.rank_math_description} />
                <meta name="twitter:label1" content="Written by" />
                <meta name="twitter:data1" content={item?._embedded?.author?.[0]?.name} />
                <meta name="twitter:label2" content="Time to read" />
                <meta name="twitter:data2" content="10 minutes" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:image" content={item?.jetpack_featured_media_url} />
                <meta name="twitter:image:alt" content={item?.metadata?.rank_math_title} />
                <meta name="twitter:site" content="UnivestIN" />
                <meta name="twitter:creator" content="UnivestIN" />
                <script
                    type="application/ld+json"
                    class="rank-math-schema-pro"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@graph": [
                                {
                                    "@type": ["Person", "Organization"],
                                    "@id": "https://univest.in/blogs/",
                                    name: "Univest",
                                    logo: {
                                        "@type": "ImageObject",
                                        "@id": "https://univest.in/blogs/#logo",
                                        url: "https://univest.in/blogs/wp-content/uploads/2023/12/left-logo.png",
                                        contentUrl: "https://univest.in/blogs/wp-content/uploads/2023/12/left-logo.png",
                                        caption: "https://univest.in/",
                                        inLanguage: "en-US",
                                        width: "187",
                                        height: "45"
                                    },
                                    image: {
                                        "@type": "ImageObject",
                                        "@id": "https://univest.in/blogs/#logo",
                                        url: "https://univest.in/blogs/wp-content/uploads/2023/12/left-logo.png",
                                        contentUrl: "https://univest.in/blogs/wp-content/uploads/2023/12/left-logo.png",
                                        caption: "https://univest.in/",
                                        inLanguage: "en-US",
                                        width: "187",
                                        height: "45"
                                    }
                                },
                                {
                                    "@type": "WebSite",
                                    "@id": "https://univest.in/blogs/#website",
                                    url: "https://univest.in/blogs",
                                    name: "https://univest.in/",
                                    publisher: { "@id": "https://univest.in/blogs/#person" },
                                    inLanguage: "en-US"
                                },
                                {
                                    "@type": "ImageObject",
                                    "@id": item?.jetpack_featured_media_url,
                                    url: item?.jetpack_featured_media_url,
                                    width: "1200",
                                    height: "628",
                                    caption: item?.metadata?.rank_math_title,
                                    inLanguage: "en-US"
                                },
                                {
                                    "@type": "WebPage",
                                    "@id": changeBlogs2ToBlogs(item?.link),
                                    url: changeBlogs2ToBlogs(item?.link),
                                    name: item?.metadata?.rank_math_title,
                                    datePublished: `${item?.date}+05:30`,
                                    dateModified: `${item?.modified}+05:30`,
                                    isPartOf: { "@id": "https://univest.in/blogs/#website" },
                                    primaryImageOfPage: { "@id": item?.jetpack_featured_media_url },
                                    inLanguage: "en-US",
                                    breadcrumb: { "@id": changeBlogs2ToBlogs(item?.link) }
                                },
                                {
                                    "@type": "Person",
                                    "@id": changeBlogs2ToBlogs(item?.link),
                                    name: item?._embedded?.author?.[0]?.name,
                                    image: {
                                        "@type": "ImageObject",
                                        "@id": "https://secure.gravatar.com/avatar/088305378b78899ec767beaf8b8a0e2a?s=96&amp;d=mm&amp;r=g",
                                        url: "https://secure.gravatar.com/avatar/088305378b78899ec767beaf8b8a0e2a?s=96&amp;d=mm&amp;r=g",
                                        caption: item?._embedded?.author?.[0]?.name,
                                        inLanguage: "en-US"
                                    }
                                },
                                {
                                    "@type": "NewsArticle",
                                    headline: item?.metadata?.rank_math_title,
                                    description: item?.metadata?.rank_math_description,
                                    author: {
                                        "@id": changeBlogs2ToBlogs(item?.link),
                                        name: item?._embedded?.author?.[0]?.name
                                    },
                                    copyrightYear: "2024",
                                    name: item?.metadata?.rank_math_title,
                                    articleSection: "Stocks",
                                    subjectOf: [
                                        {
                                            ...unserializeData(item?.metadata?.rank_math_schema_FAQPage),
                                            datePublished: `${item?.date}+05:30`,
                                            dateModified: `${item?.modified}+05:30`,
                                            metadata: undefined,
                                            url: changeBlogs2ToBlogs(item?.link)
                                        }
                                    ],
                                    "@id": changeBlogs2ToBlogs(item?.link),
                                    isPartOf: { "@id": changeBlogs2ToBlogs(item?.link) },
                                    publisher: { "@id": "https://univest.in/blogs/#person" },
                                    image: { "@id": item?.jetpack_featured_media_url },
                                    inLanguage: "en-US",
                                    mainEntityOfPage: { "@id": changeBlogs2ToBlogs(item?.link) }
                                },
                                {
                                    "@type": "Organization",
                                    "@id": "https://univest.in/#organization",
                                    url: "https://univest.in/blogs",
                                    name: "Univest",
                                    logo: "https://storage.googleapis.com/app-assets-univest/website-assets/UnivestLogo.jpg",
                                    address: {
                                        "@type": "PostalAddress",
                                        addressLocality: "Gurugram, Haryana",
                                        postalCode: "122002",
                                        streetAddress:
                                            "10, Poorvi Marg, DLF Phase 2, Sector 25, Gurugram, Haryana 122002"
                                    },
                                    email: "support@univest.in",
                                    telephone: "+91-8800049790",
                                    sameAs: [
                                        "https://www.facebook.com/Univest.in",
                                        "https://www.instagram.com/univest.in/",
                                        "https://twitter.com/UnivestIN",
                                        "http://linkedin.com/company/univest-in",
                                        "https://www.youtube.com/channel/UCRlFx-WxA4yY7Y33SRZaQ4w",
                                        "https://www.crunchbase.com/organization/univest-557f"
                                    ],
                                    contactPoint: [
                                        {
                                            "@type": "ContactPoint",
                                            telephone: "+91-8800049790",
                                            contactType: "Customer Service"
                                        }
                                    ],
                                    foundingDate: "2022",
                                    founders: [
                                        {
                                            "@type": "Person",
                                            name: "Pranit Arora",
                                            id: "https://in.linkedin.com/in/pranit-arora-1b505422"
                                        },
                                        {
                                            "@type": "Person",
                                            name: "Avneet Dhamija",
                                            id: "https://in.linkedin.com/in/avneet-dhamija"
                                        },
                                        {
                                            "@type": "Person",
                                            name: "Vikash Agarwal",
                                            id: "https://in.linkedin.com/in/vikashagrawal1985"
                                        }
                                    ],
                                    funder: {
                                        "@type": "Corporation",
                                        legalName: "Trinity media Group",
                                        url: "https://www.vccircle.com/company/trinity-media-group"
                                    }
                                },
                                {
                                    "@type": "Product",
                                    "@id": "https://univest.in/pro/#product",
                                    name: "Stock Advisory service In India",
                                    url: "https://univest.in/pro",
                                    image: "",
                                    review: [
                                        {
                                            "@type": "Review",
                                            reviewBody:
                                                "I like Today's Gainers and Today's Losers in Discover in the Invest section.It helps me plan which Stocks should I invest and which I should not",
                                            author: { "@type": "Person", name: "Baradi Shivakumar" },
                                            reviewRating: { "@type": "Rating", ratingValue: "4.9" },
                                            datePublished: "2023"
                                        },
                                        {
                                            "@type": "Review",
                                            reviewBody:
                                                "I like the Portfolio News in the Invest section on the univest App. It shows News related to the Stocks that are in my Portfolio which helps me in keeping a track of my Stocks status",
                                            author: { "@type": "Person", name: "Naman Nema" },
                                            reviewRating: { "@type": "Rating", ratingValue: "4.9" },
                                            datePublished: "2023"
                                        },
                                        {
                                            "@type": "Review",
                                            reviewBody:
                                                "I like the option chain section and the way Greeks are displayed as it is very simple to track and make decision to execute orders. It is also easy to buy and sell positions, UI is very nice.",
                                            author: { "@type": "Person", name: "Shiva singh" },
                                            reviewRating: { "@type": "Rating", ratingValue: "4.9" },
                                            datePublished: "2022"
                                        },
                                        {
                                            "@type": "Review",
                                            reviewBody: "Where trust matters univest never breaks.",
                                            author: { "@type": "Person", name: "Jatin" },
                                            reviewRating: { "@type": "Rating", ratingValue: "4.9" },
                                            datePublished: "2023"
                                        },
                                        {
                                            "@type": "Review",
                                            reviewBody:
                                                "The ROS feature is very good and can help the budding option traders to learn with proper discipline and risk management.",
                                            author: { "@type": "Person", name: "Pandi Donald" },
                                            reviewRating: { "@type": "Rating", ratingValue: "4.9" },
                                            datePublished: "2023"
                                        },
                                        {
                                            "@type": "Review",
                                            reviewBody:
                                                "I have been using univest for over 5 years now and it has provided me with an awesome experience and excellent customer support. Thanks to univest, I have also gained a lot of knowledge about stock market.",
                                            author: { "@type": "Person", name: "Prabhakar Dutta" },
                                            reviewRating: { "@type": "Rating", ratingValue: "4.9" },
                                            datePublished: "2023"
                                        },
                                        {
                                            "@type": "Review",
                                            reviewBody:
                                                "I like the Portfolio News in the Invest section on the univest App. It shows News related to the Stocks that are in my Portfolio which helps me in keeping a track of my Stocks status",
                                            author: { "@type": "Person", name: "Pandi Donald" },
                                            reviewRating: { "@type": "Rating", ratingValue: "4.9" },
                                            datePublished: "2023"
                                        }
                                    ],
                                    brand: "univest",
                                    description:
                                        "Start Trading in Share Market, SIP, IPOs, Mutual Fund, Indices and Commodity at univest.in with hassle free process.",
                                    aggregateRating: {
                                        "@type": "AggregateRating",
                                        ratingValue: "4.4",
                                        ratingCount: "230120",
                                        reviewCount: "197000"
                                    }
                                }
                            ]
                        })
                    }}
                />
            </Head>
            <div className="md:grid grid-cols-12 max-w-screen-xl items-start py-16 lg:pt-24 lg:pb-0 mx-auto lg:px-8 ">
                <div className="font-Inter mx-4 my-8 col-span-12 lg:col-span-9 relative">
                    <nav className="w-full absolute -top-10 hidden md:block">
                        <ol className="list-reset flex">
                            <li>
                                <Link href="/" className="text-primary text-xs font-medium">
                                    Home
                                </Link>
                            </li>
                            <li className=" flex items-end mb-1 px-2">
                                <AiOutlineRight color="#707070" size={14} />
                            </li>
                            <li>
                                <Link href="/blogs" className="text-primary text-xs font-medium">
                                    Blogs
                                </Link>
                            </li>
                            <li className=" flex items-end mb-1 px-2">
                                <AiOutlineRight color="#707070" size={14} />
                            </li>
                            <li>
                                <span className="text-primary text-xs font-medium">{item?.title?.rendered}</span>
                            </li>
                        </ol>
                    </nav>
                    <h1
                        className="text-3xl font-bold mb-4"
                        dangerouslySetInnerHTML={{ __html: item?.title?.rendered }}
                    />
                    <div className="flex flex-col md:flex-row  gap-2 mb-2 justify-between">
                        <p className="text-[rgba(12,17,43,0.9)] text-sm">
                            Posted by : <b>{item?._embedded?.author?.[0]?.name}</b> |{" "}
                            <b>{new Date(item?.date).toDateString()}</b>
                        </p>
                        <div className="flex text-center lg:justify-center justify-start items-center gap-2">
                            <WhatsappShareButton
                                url={changeBlogs2ToBlogs(item?.link)}
                                className="w-5 min-w-5"
                                title={item?.title?.rendered}
                            >
                                <RiWhatsappFill className="text-green-500 w-5 h-5" />
                            </WhatsappShareButton>
                            <FacebookShareButton
                                url={changeBlogs2ToBlogs(item?.link)}
                                className="w-5 min-w-5"
                                title={item?.title?.rendered}
                            >
                                <FaFacebook className="text-blue-500 w-4 h-4" />
                            </FacebookShareButton>
                            <TwitterShareButton
                                url={changeBlogs2ToBlogs(item?.link)}
                                className="w-5 min-w-5"
                                title={item?.title?.rendered}
                            >
                                <FaXTwitter className="w-4 h-4" />
                            </TwitterShareButton>
                            <LinkedinShareButton
                                url={changeBlogs2ToBlogs(item?.link)}
                                className="w-5 min-w-5"
                                title={item?.title?.rendered}
                            >
                                <FaLinkedin className="text-blue-500 w-4 h-4" />
                            </LinkedinShareButton>
                        </div>
                    </div>
                    {item?.featured_media && (
                        <Image
                            className=" w-full aspect-[320/160] mb-8"
                            width={500}
                            height={300}
                            src={item?.jetpack_featured_media_url}
                            alt={item?.title?.rendered}
                        />
                    )}
                    <div
                        id="blog-details"
                        className="prose max-w-none mt-4"
                        dangerouslySetInnerHTML={{ __html: item.content?.rendered }}
                    />
                </div>

                <div className="col-span-12 lg:col-span-3  mt-4 lg:mt-10 p-4 sticky top-24">
                    {webview === "true" ? (
                        ""
                    ) : (
                        <div className="w-full border rounded-md p-4">
                            <Image
                                src={`https://storage.googleapis.com/production-univest/pn_images/1737551279248_new_5_free_trial.png?a=${moment().format(
                                    "hh"
                                )}`}
                                width={400}
                                height={400}
                                className="w-full cursor-pointer"
                                alt="banner"
                                onClick={() => popUp.open(userDetail)}
                            />
                            <div className=" hidden  h-10 lg:h-14 md:flex items-center justify-center bg-[#FFF] rounded-full border border-[#606060] text-xs lg:text-base font-medium">
                                <BlackButton
                                    className="whitespace-nowrap px-3 lg:px-6 h-full ml-auto w-full max-w-sm"
                                    text={
                                        userDetail?.userData?.authToken ? "Activate free trial" : "Activate free trial"
                                    }
                                    onClick={() => popUp.open(userDetail)}
                                />
                            </div>
                        </div>
                    )}
                    <SideBarBlogServe item={item} relatedData={relatedData} />
                </div>
            </div>
        </>
    );
}
